import type * as fsType from 'fs/promises'

import {
  HttpHeaders,
  MimeTypes,
  bearerAuthHeader,
} from '@climatepartner/unified-api-lib-http-utils'

import { SecureTokenServiceApi } from '../../publish/sts/apis/secure-token-service-api'
import { Configuration } from '../../publish/sts/configuration'
import {
  CreateOrderErrorCode,
  UnifiedApiClientError,
  UnifiedApiError,
} from '../types'

// Browser support
let fs: typeof fsType
if (typeof window === 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  fs = require('fs/promises')
}

interface Token {
  expires: number
  token: string
}

export class StsService extends SecureTokenServiceApi {
  #cpinsToken?: Token

  constructor(
    protected basePath: string,
    private longtermToken: string,
    private authorizationStoragePath?: string,
  ) {
    super(new Configuration({ basePath: basePath }))
  }

  /**
   * get the access token. If token is expired a new token will be fetched
   * and stored.
   */
  getToken = async (force = false): Promise<string> => {
    if (this.authorizationStoragePath) {
      this.#cpinsToken = await this.#readTokenFromDisk(
        this.authorizationStoragePath,
      )
    }
    if (!force && this.#validateToken()) {
      return this.#cpinsToken!.token
    }

    let response
    try {
      response = await this.token(
        {
          body: {},
          disableCache: force ? 'true' : undefined,
        },
        {
          headers: {
            [HttpHeaders.AUTHORIZATION]: bearerAuthHeader(this.longtermToken),
            [HttpHeaders.CONTENT_TYPE]: MimeTypes.APPLICATION_JSON,
          },
        },
      )
    } catch (e) {
      // The default response interceptor configured in sdk index.ts will throw an UnifiedApiError if error code is >= 400
      if (e instanceof UnifiedApiError) {
        throw e
      }

      throw new UnifiedApiClientError(
        CreateOrderErrorCode.INTERNAL_ERROR,
        (e as Error).message,
      )
    }

    await this.#updateToken({
      expires: Date.now() + response.data.result.expires_in * 1000 - 60000,
      token: response.data.result.access_token,
    })

    return response.data.result.access_token
  }

  #readTokenFromDisk = async (
    tokenStoragePath: string,
  ): Promise<Token | undefined> => {
    // Browser support
    if (typeof window === 'undefined') {
      const exists = await fs.access(tokenStoragePath).then(
        () => true,
        () => false,
      )
      if (exists) {
        const token = await fs.readFile(tokenStoragePath, 'utf8')
        if (token) {
          return JSON.parse(token)
        }
      }
    }
  }

  #updateToken = async (token: Token) => {
    // Browser support
    if (typeof window === 'undefined') {
      if (this.authorizationStoragePath) {
        await fs.writeFile(this.authorizationStoragePath, JSON.stringify(token))
      }
    }
    this.#cpinsToken = token
  }

  #validateToken = (): boolean => {
    if (this.#cpinsToken) {
      return this.#cpinsToken.expires - Date.now() >= 0
    }

    return false
  }
}
